import { useEffect, useMemo, useState } from "react";
import type { Node } from "@xyflow/react";
import type { OperationCanvasNodeData } from "../OperationCanvasNode.js";
import { apiFetch } from "../../lib/api.js";
import { PDO_READ_MODE_LABELS, writeModeDisplayLabel } from "../../lib/pdoUiLabels.js";
import { NodeOutputStats, type NodeOutputRecord } from "./nodeInspectorWidgets.js";
import { TanTableViz } from "../TanTableViz.js";
import { queryResultsToTanTableModel } from "../../lib/queryResultsToTanTable.js";
import { InspectorTabs } from "../inspector/InspectorTabs.js";
import type { QueryResultsPayload } from "../QueryResultsGrid.js";

type ReadMode = "current" | "version" | "as_of_business" | "as_of_system" | "history";

type PdoOption = {
  id: string;
  name: string;
  slug: string;
  status: "active" | "draft" | "archived";
  write_mode: string | null;
  current_version: number | null;
  current_row_count: number | null;
};

export function OperationInspectorPersistentProvider({
  selectedNode,
  setNodeData,
  nodeOutputRecord,
  onRunNode,
  runBusy,
  operationStatus,
  cachedPayload,
  cacheLoading,
}: {
  selectedNode: Node<OperationCanvasNodeData>;
  setNodeData: (nodeId: string, updater: (data: OperationCanvasNodeData) => OperationCanvasNodeData) => void;
  nodeOutputRecord?: NodeOutputRecord;
  onRunNode: (nodeId: string) => void | Promise<void>;
  runBusy: boolean;
  operationStatus: string;
  cachedPayload: QueryResultsPayload | null | undefined;
  cacheLoading: boolean;
}) {
  const [pdos, setPdos] = useState<PdoOption[]>([]);
  const [pdosLoading, setPdosLoading] = useState(true);

  const config = selectedNode.data.config ?? {};
  const spec = (config.spec_persistent_provider as Record<string, unknown> | undefined) ?? {};

  const sourcePdoId = (spec.source_pdo_id as string | undefined) ?? "";
  const readMode = ((spec.read_mode as string | undefined) ?? "current") as ReadMode;
  const versionNumber = (spec.version_number as number | undefined) ?? null;
  const asOfBusinessTime = (spec.as_of_business_time as string | undefined) ?? "";
  const asOfSystemTime = (spec.as_of_system_time as string | undefined) ?? "";

  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      setPdosLoading(true);
      try {
        const data = await apiFetch<{ pdos: PdoOption[] }>("/api/tenant/pdos");
        setPdos(data.pdos.filter((p) => p.status !== "archived"));
      } catch (e) {
        setLoadError(`Could not load PDOs: ${e instanceof Error ? e.message : String(e)}`);
        setPdos([]);
      } finally {
        setPdosLoading(false);
      }
    })();
  }, []);

  function updateSpec(patch: Record<string, unknown>) {
    setNodeData(selectedNode.id, (data) => ({
      ...data,
      config: {
        ...data.config,
        spec_persistent_provider: {
          source_pdo_id: sourcePdoId,
          read_mode: readMode,
          version_number: versionNumber,
          as_of_business_time: asOfBusinessTime || null,
          as_of_system_time: asOfSystemTime || null,
          ...patch,
        },
      },
    }));
  }

  const selectedPdo = pdos.find((p) => p.id === sourcePdoId);

  const cachedTanModel = useMemo(
    () => (cachedPayload ? queryResultsToTanTableModel(cachedPayload) : null),
    [cachedPayload]
  );

  return (
    <InspectorTabs
      surfaceId="operationEditor"
      tabGroupId="persistent_provider"
      tabs={[
        {
          slot: "setup",
          content: (
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 12 }}>
      {/* Source PDO */}
      <div className="field">
        <label className="label">Source PDO</label>
        {pdosLoading ? (
          <select className="select" disabled>
            <option>Loading…</option>
          </select>
        ) : (
          <select
            className="select"
            value={sourcePdoId}
            onChange={(e) =>
              updateSpec({
                source_pdo_id: e.target.value,
                read_mode: "current",
                version_number: null,
                as_of_business_time: null,
                as_of_system_time: null,
              })
            }
          >
            <option value="">— Select a PDO —</option>
            {pdos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name || p.slug}
                {p.status === "draft" ? " (draft)" : ""}
                {p.current_version != null ? ` · v${p.current_version}` : ""}
                {p.current_row_count != null ? ` · ${p.current_row_count.toLocaleString()} rows` : ""}
              </option>
            ))}
          </select>
        )}
        {loadError ? (
          <div className="muted" style={{ fontSize: 12, color: "var(--danger-text)", marginTop: 4 }}>{loadError}</div>
        ) : null}
        {selectedPdo ? (
          <span className="help">
            <span className={selectedPdo.status === "active" ? "pillOk" : "pillWarn"} style={{ fontSize: 11 }}>
              {selectedPdo.status}
            </span>
            {selectedPdo.write_mode ? (
              <span style={{ marginLeft: 6, fontSize: 11 }}>{writeModeDisplayLabel(selectedPdo.write_mode)}</span>
            ) : null}
          </span>
        ) : null}
      </div>

      {/* Read Mode */}
      <div className="field">
        <label className="label">Read Mode</label>
        <select
          className="select"
          value={readMode}
          onChange={(e) =>
            updateSpec({
              read_mode: e.target.value,
              version_number: null,
              as_of_business_time: null,
              as_of_system_time: null,
            })
          }
        >
          <option value="current">{PDO_READ_MODE_LABELS.current}</option>
          <option value="version">{PDO_READ_MODE_LABELS.version}</option>
          <option value="as_of_business">{PDO_READ_MODE_LABELS.as_of_business}</option>
          <option value="as_of_system">{PDO_READ_MODE_LABELS.as_of_system}</option>
          <option value="history">{PDO_READ_MODE_LABELS.history}</option>
        </select>
      </div>

      {/* Conditional fields */}
      {readMode === "version" ? (
        <div className="field">
          <label className="label">Version Number</label>
          <input
            className="input mono"
            type="number"
            min={1}
            value={versionNumber ?? ""}
            onChange={(e) =>
              updateSpec({ version_number: e.target.value ? parseInt(e.target.value, 10) : null })
            }
            placeholder="e.g. 3"
          />
        </div>
      ) : null}

      {readMode === "as_of_business" ? (
        <div className="field">
          <label className="label">As-Of Business Time</label>
          <input
            className="input mono"
            type="datetime-local"
            value={asOfBusinessTime ? asOfBusinessTime.replace("Z", "").slice(0, 16) : ""}
            onChange={(e) =>
              updateSpec({ as_of_business_time: e.target.value ? `${e.target.value}:00Z` : null })
            }
          />
          <span className="help">Returns rows valid at this business time</span>
        </div>
      ) : null}

      {readMode === "as_of_system" ? (
        <div className="field">
          <label className="label">As-Of System Time</label>
          <input
            className="input mono"
            type="datetime-local"
            value={asOfSystemTime ? asOfSystemTime.replace("Z", "").slice(0, 16) : ""}
            onChange={(e) =>
              updateSpec({ as_of_system_time: e.target.value ? `${e.target.value}:00Z` : null })
            }
          />
          <span className="help">Returns rows observed at this system time</span>
        </div>
      ) : null}

      {/* Read mode descriptions */}
      <div className="muted" style={{ fontSize: 12 }}>
        {readMode === "current" && "Reads the current (latest) warm version."}
        {readMode === "version" && "Reads a specific version by number."}
        {readMode === "as_of_business" && "Reads rows valid at the given business timestamp (merge_history only)."}
        {readMode === "as_of_system" && "Reads rows observed at the given system timestamp (merge_history only)."}
        {readMode === "history" && "Reads every version an Atlas Report run kept, newest first — each row says which run (snapshot datasets only)."}
      </div>
            </div>
          ),
        },
        {
          slot: "data",
          content: (
            <div className="card" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <strong>
            Cached data
            {cachedPayload?.rowCount != null ? ` — ${cachedPayload.rowCount.toLocaleString()} rows` : ""}
          </strong>
          <button
            className="btn btnSm"
            type="button"
            disabled={runBusy || operationStatus === "validate" || !sourcePdoId}
            onClick={() => void onRunNode(selectedNode.id)}
            title={
              operationStatus === "validate"
                ? "Operation requires validation"
                : !sourcePdoId
                  ? "Select a PDO first"
                  : "Read the PDO and refresh this node's cache"
            }
          >
            {runBusy ? "Running…" : "Run this node"}
          </button>
        </div>
        <NodeOutputStats rec={nodeOutputRecord} />
        {cacheLoading ? (
          <div className="muted">Loading cache…</div>
        ) : cachedTanModel === null ? (
          <div className="muted">No cached data. Click &quot;Run this node&quot; to read the PDO and populate the cache.</div>
        ) : (
          <div className="inspectorTanTableHost">
            <TanTableViz model={cachedTanModel} title={selectedPdo?.name || selectedPdo?.slug} />
          </div>
        )}
            </div>
          ),
        },
      ]}
    />
  );
}
